import { Link } from 'react-router-dom'
import { Image as ImageIcon } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { GradientHoverCard } from '@/components/common/GradientHoverCard'
import { useServices } from '../hooks/useServices'
import { buildServiceBookingUrl } from '../lib/bookingUrl'
import type { ServiceDocument, PricingType } from '@/types'

function formatPrice(pricingType: PricingType, price?: number, priceMax?: number): string {
  switch (pricingType) {
    case 'flat':        return price !== undefined ? `$${price}` : ''
    case 'hourly':      return price !== undefined ? `$${price}/hr` : ''
    case 'perMinute':   return price !== undefined ? `$${price}/min` : ''
    case 'perHeadshot': return price !== undefined ? `$${price}/headshot` : ''
    case 'starting_at': return price !== undefined ? `Starting at $${price}` : ''
    case 'range':       return price !== undefined && priceMax !== undefined ? `$${price}–$${priceMax}` : ''
    case 'contact':     return 'Contact for pricing'
  }
}

function ServiceCard({ service }: { service: ServiceDocument & { id: string } }) {
  const priceDisplay = formatPrice(service.pricingType, service.price, service.priceMax)

  return (
    <GradientHoverCard className="rounded-xl">
    <Link
      id={service.id}
      to={buildServiceBookingUrl(service)}
      className="group flex flex-col m-[2px] overflow-hidden rounded-[10px] transition-colors"
    >
      <div className="relative aspect-[16/10] overflow-hidden bg-gradient-to-br from-muted/40 via-muted/20 to-background">
        {service.photoUrl?.trim() ? (
          <img
            src={service.photoUrl}
            alt={service.title}
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            style={{
              objectPosition: `${(service.photoFocusX ?? 0.5) * 100}% ${(service.photoFocusY ?? 0.5) * 100}%`,
            }}
            loading="lazy"
          />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
            <ImageIcon className="h-10 w-10 text-muted-foreground/30" strokeWidth={1} />
            <span className="px-4 text-center text-xs font-medium uppercase tracking-widest text-muted-foreground/50">
              {service.title}
            </span>
          </div>
        )}
      </div>
      <div className="flex flex-1 flex-col space-y-3 p-6">
      <div>
        <h3 className="text-lg font-semibold">{service.title}</h3>
        {service.category && (
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mt-0.5">
            {service.category}
          </p>
        )}
      </div>

      <p className="text-sm text-muted-foreground leading-relaxed flex-1">
        {service.description}
      </p>

      <div className="flex items-center justify-between gap-4 pt-1">
        <div>
          {priceDisplay && (
            <p className="text-sm font-semibold">{priceDisplay}</p>
          )}
          {service.pricingNote && (
            <p className="text-xs text-muted-foreground">{service.pricingNote}</p>
          )}
        </div>

        <div className="flex items-center gap-2">
          {(service.ctaUrl || service.ctaLabel) && service.ctaUrl ? (
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                window.open(service.ctaUrl, '_blank', 'noopener,noreferrer')
              }}
            >
              {service.ctaLabel || 'Contact Us'}
            </Button>
          ) : null}
          <Button size="sm" tabIndex={-1} aria-hidden="true">
            Book Now
          </Button>
        </div>
      </div>
      </div>
    </Link>
    </GradientHoverCard>
  )
}

export function ServicesPage() {
  const { services, categories: categoryDocs, loading, error } = useServices()

  // Group services by category, ordered by the category admin sort order.
  // Any category titles referenced by services but not present in the
  // serviceCategories collection get appended at the end in first-seen order.
  const categoryOrder = new Map<string, number>()
  categoryDocs.forEach((c, i) => categoryOrder.set(c.title, i))

  const byCategory: Record<string, Array<ServiceDocument & { id: string }>> = {}
  const seen: string[] = []
  for (const service of services) {
    if (!byCategory[service.category]) {
      byCategory[service.category] = []
      seen.push(service.category)
    }
    byCategory[service.category]!.push(service)
  }

  const categories = seen.sort((a, b) => {
    const ai = categoryOrder.has(a) ? categoryOrder.get(a)! : Number.POSITIVE_INFINITY
    const bi = categoryOrder.has(b) ? categoryOrder.get(b)! : Number.POSITIVE_INFINITY
    if (ai !== bi) return ai - bi
    return seen.indexOf(a) - seen.indexOf(b)
  })

  return (
    <div className="container mx-auto px-4 py-16">
      <div className="mb-12 flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
        <div className="max-w-2xl">
          <p className="mb-3 text-sm font-semibold uppercase tracking-widest text-brand-orange">
            Our Services
          </p>
          <h1 className="mb-4 text-4xl font-bold tracking-tight">
            High-quality media services by dancers, for dancers.
          </h1>
          <p className="text-muted-foreground leading-relaxed">
            We know the art from the inside out. Every project helps fund new choreographic
            opportunities for emerging artists.
          </p>
        </div>
        <div className="shrink-0">
          <Link to="/request">
            <Button>Book a Service</Button>
          </Link>
        </div>
      </div>

      {loading && (
        <div className="space-y-10">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="space-y-4">
              <Skeleton className="h-6 w-32" />
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {Array.from({ length: 3 }).map((_, j) => (
                  <Skeleton key={j} className="h-48 w-full rounded-lg" />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {error && <p className="text-sm text-destructive">{error}</p>}

      {!loading && !error && services.length === 0 && (
        <p className="text-sm text-muted-foreground">No services available at this time.</p>
      )}

      {!loading && !error && services.length > 0 && (
        <div className="space-y-12">
          {categories.map((category) => (
            <section key={category} className="space-y-4">
              <h2 className="text-2xl font-bold">{category}</h2>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {byCategory[category]!.map((service) => (
                  <ServiceCard key={service.id} service={service} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  )
}
